import React, { useState, useCallback } from 'react';
import type { MutationUpdaterFn } from 'apollo-client';
import { useMutation } from '@apollo/react-hooks';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import { useFlags } from '@atlaskit/flag';
import { R400 } from '@atlaskit/theme/colors';
import ErrorIcon from '@atlaskit/icon/glyph/error';

import { getInitialPageSize } from '../../initialPageSizeConstant';
import { LikeMutation } from '../LikeMutation.graphql';
import { UnlikeMutation } from '../UnlikeMutation.graphql';
import { FeedPopularFeedQuery } from '../../FeedPopularFeedQuery.graphql';
import type {
	LikeMutation as LikeMutationType,
	LikeMutationVariables,
} from '../__types__/LikeMutation';
import type {
	UnlikeMutation as UnlikeMutationType,
	UnlikeMutationVariables,
} from '../__types__/UnlikeMutation';
import type {
	FeedPopularFeedQuery as FeedPopularFeedQueryType,
	FeedPopularFeedQueryVariables,
} from '../../__types__/FeedPopularFeedQuery';

const i18n = defineMessages({
	likeMutationErrorTitle: {
		id: 'feed.card.like.mutation.error.title',
		defaultMessage: 'We’re having trouble liking that page',
		description:
			'Title of error flag shown when a user attempts to like the feed card but the action fails.',
	},
	unlikeMutationErrorTitle: {
		id: 'feed.card.unlike.mutation.error.title',
		defaultMessage: 'We’re having trouble unliking that page',
		description:
			'Title of error flag shown when a user attempts to unlike the feed card but the action fails.',
	},
	likeMutationErrorDescription: {
		id: 'feed.card.like.mutation.error.description',
		defaultMessage: 'Try it again.',
		description:
			'Body of error flag shown when a user attempts to like/unlike the feed card but the action fails. Shown below the error title',
	},
});

const queryVariables = {
	first: getInitialPageSize(),
};

const updatePopularFeedCache: MutationUpdaterFn = (cache, { data }) => {
	const contentId = data?.contentId;
	const count = data?.count;
	const userLiked = data?.userLiked;
	const contentType = data?.type;
	const cachedata = cache.readQuery<FeedPopularFeedQueryType, FeedPopularFeedQueryVariables>({
		query: FeedPopularFeedQuery,
		variables: queryVariables,
	});
	if (contentId && contentType && count !== null && userLiked !== null && cachedata) {
		const newData: FeedPopularFeedQueryType = { ...cachedata };
		newData.popularFeed.nodes.forEach((node) => {
			if (node.content.id === contentId && node.content.likes) {
				node.content.likes.count = count;
				node.content.likes.currentUserLikes = userLiked;
			}
		});

		cache.writeQuery<FeedPopularFeedQueryType, FeedPopularFeedQueryVariables>({
			query: FeedPopularFeedQuery,
			variables: queryVariables,
			data: newData,
		});
	}
};

const updatePopularFeedCacheLike: MutationUpdaterFn<LikeMutationType> = (cache, { data }) => {
	updatePopularFeedCache(cache, {
		data: {
			contentId: data?.likeContent.content.id,
			count: data?.likeContent.content.likes?.count,
			userLiked: data?.likeContent.content.likes?.currentUserLikes,
			type: data?.likeContent.content.type,
		},
	});
};

const updatePopularFeedCacheUnlike: MutationUpdaterFn<UnlikeMutationType> = (cache, { data }) => {
	updatePopularFeedCache(cache, {
		data: {
			contentId: data?.unlikeContent.content.id,
			count: data?.unlikeContent.content.likes?.count,
			userLiked: data?.unlikeContent.content.likes?.currentUserLikes,
			type: data?.unlikeContent.content.type,
		},
	});
};

export function usePopularContentLike(
	initialLikes: number,
	initialUserLiked: boolean,
	contentId: string,
) {
	const [numLikes, setNumLikes] = useState(initialLikes);
	const [userLiked, setUserLike] = useState(initialUserLiked);
	const { showFlag } = useFlags();

	const onMutationError = useCallback(() => {
		showFlag({
			description: <FormattedMessage {...i18n.likeMutationErrorDescription} />,
			title: (
				<FormattedMessage
					{...(userLiked ? i18n.likeMutationErrorTitle : i18n.unlikeMutationErrorTitle)}
				/>
			),
			icon: <ErrorIcon label="error" primaryColor={token('color.icon.danger', R400)} />,
			isAutoDismiss: true,
		});

		if (userLiked) {
			setNumLikes((l) => l - 1);
		} else {
			setNumLikes((l) => l + 1);
		}
		setUserLike((ul) => !ul);
	}, [showFlag, userLiked]);

	const [mutateLike, { error: likeError }] = useMutation<LikeMutationType, LikeMutationVariables>(
		LikeMutation,
		{
			variables: { contentId },
			update: updatePopularFeedCacheLike,
			onError: onMutationError,
		},
	);
	const [mutateUnlike, { error: unlikeError }] = useMutation<
		UnlikeMutationType,
		UnlikeMutationVariables
	>(UnlikeMutation, {
		variables: { contentId },
		update: updatePopularFeedCacheUnlike,
		onError: onMutationError,
	});

	const likeMutationError = likeError || unlikeError;
	const handleLikeButton = useCallback(async () => {
		setUserLike(!userLiked);
		if (userLiked) {
			setNumLikes(numLikes - 1);
			await mutateUnlike();
		} else {
			setNumLikes(numLikes + 1);
			await mutateLike();
		}
	}, [userLiked, setUserLike, numLikes, setNumLikes, mutateLike, mutateUnlike]);

	return { numLikes, userLiked, handleLikeButton, likeMutationError };
}
